import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash';
import { useSwipeable } from 'react-swipeable';
import { useApolloClient, gql } from '@apollo/client';
import Variations from './Variations';

import { Item, ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import { statusBackgroundColor, MultiplierStyleProps } from './Order';
import ItemName from './ItemName';
import { useStorage } from '~Components/Storage';
import { usePrevious } from '~Hooks';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';
import { itemInBong } from '~Scenes/POS/Queries/__queries__';

const ItemWrapper = styled.div<{
  statusColor: string;
  highlighted: boolean;
  styleMultiplier: {
    space?: number;
  };
  hideLeftBorder: boolean;
  touched: boolean;
}>`
  color: #1c232c;
  background-color: ${(props) => (props.highlighted ? '#ffc000' : '#fff')};
  display: flex;
  flex-direction: column;
  padding: calc(16px - 0.2em * ${({ styleMultiplier }) => styleMultiplier?.space});
  box-shadow: #d5d7d8 0px 1px 0px;
  margin-bottom: 1px;
  border-left: ${({ statusColor, hideLeftBorder }) =>
    `${hideLeftBorder ? '0px' : '10px'} solid ${statusColor}`};
  &:active {
    background: #f9f9f9;
  }
  box-shadow: ${({ touched }) => (touched ? '0 0 8px 100px rgba(0,0,0,0.15) inset' : 'none')};
  transition: background ${(props) => (props.highlighted ? 0 : 40)}s linear;
`;

const OrderItemTitle = styled.div<MultiplierStyleProps & { isChild: boolean }>`
  font-size: calc(24px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  font-weight: ${({ isChild }) => (isChild ? 400 : 600)};
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const OrderItemMessage = styled.div<MultiplierStyleProps>`
  color: #1c232c;
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  font-weight: 600;
  letter-spacing: 0px;
  background-color: #e8e9ea;
  border-radius: 4px;
  padding: 4px 16px;
  margin-top: 8px;
`;

const CountBox = styled.div<MultiplierStyleProps>`
  display: flex;
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  color: #41474f;
  background-color: #e8e9ea;
  border-radius: 2px;
  min-width: calc(40px - 0.2em * ${({ styleMultiplier }) => styleMultiplier?.size});
  margin-right: 16px;
  text-align: center;
  padding: 3px;
  place-content: center;
  line-height: calc(40px - 0.25em * ${({ styleMultiplier }) => styleMultiplier?.size});
`;

const Count = styled.span`
  font-weight: 600;
  color: #41474f;
  border-radius: 2px;
  text-align: center;
`;

interface Props {
  item: Item;
  count: number;
  progressItem?: (item: Item) => void;
  disableItemProgression?: boolean;
  isBuffering: boolean;
  isChild?: boolean;
}

const OrderItem = ({
  count,
  item,
  progressItem,
  isChild,
  isBuffering,
  disableItemProgression,
}: Props) => {
  const [storage] = useStorage();
  const client = useApolloClient();

  // This is true when we are touching an item and haven't moved our finger.
  // In order words, we're touching but not scrolling.
  const [touching, setTouching] = useState(false);
  const withVariations = !isEmpty(item.variations) || !isEmpty(item.removedVariations);

  const itemFragment = storage.markAddedVariationsGreen
    ? client.readFragment<itemInBong>({
        id: `Item${item.itemId}`,
        fragment: ITEM_FRAGMENT,
      })
    : null;

  // When we are in "buffering" mode and the count of a row is changed, we
  // want to highlight this for the user so they dont miss it.
  // We do this by a) Highlighting the row in yellow which slowly fades out and
  // b) by showing a "*" next to the count.
  const prevCount = usePrevious(count);
  const [highlighted, setHighlighted] = useState(false);
  const [hasChangedCount, setHasChangedCount] = useState(false);

  useEffect(() => {
    // This logic just checks if the count has increased,
    // without triggering on the initial render.
    if (!prevCount || prevCount > count) {
      return;
    }

    if (!isBuffering) {
      return;
    }

    setHighlighted(true);
    setHasChangedCount(true);

    const id = setTimeout(() => {
      setHighlighted(false);
    }, 100);

    return () => clearTimeout(id);
  }, [count]);

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => setTouching(false),
  });

  const handleItemTouch = (item: Item) => {
    // touching is false when scrolling. Prevents accidental touches.
    if (!touching || disableItemProgression || !progressItem) {
      return;
    }
    progressItem && progressItem(item);
    setTouching(false);
  };

  return (
    <ItemWrapper
      styleMultiplier={storage.styleMultiplier}
      key={item.id}
      {...(disableItemProgression ? {} : handlers)}
      highlighted={highlighted}
      onTouchStart={(e) => {
        setTouching(true);
      }}
      touched={!disableItemProgression && touching}
      onTouchEnd={() => handleItemTouch(item)}
      statusColor={statusBackgroundColor(item.status as ORDER_STATUS)}
      hideLeftBorder={!!disableItemProgression}>
      <TitleWrapper>
        <CountBox styleMultiplier={storage.styleMultiplier}>
          <Count>
            {count} {hasChangedCount && isBuffering && '*'}
          </Count>
        </CountBox>
        <OrderItemTitle styleMultiplier={storage.styleMultiplier} isChild={isChild || false}>
          <ItemName id={item.itemId} />
          {withVariations && ':'}
          {item.comment && (
            <OrderItemMessage styleMultiplier={storage.styleMultiplier}>
              {item.comment}
            </OrderItemMessage>
          )}
        </OrderItemTitle>
      </TitleWrapper>
      {withVariations && (
        <Variations
          modificationGroups={itemFragment?.modificationGroups || []}
          variations={item.variations}
          removedVariations={item.removedVariations}
        />
      )}
    </ItemWrapper>
  );
};

export default React.memo(OrderItem);
